import { useEffect, useState, useRef } from 'react';
import { useSettings } from '@backpackjs/storefront';
import { useRouter } from 'next/router';
import { throttle } from 'lodash';

import { BeamSelectNonprofit } from '../Beam';
import { CartEmpty } from './CartEmpty';
import { CartHeader } from './CartHeader';
import { CartItem } from './CartItem';
import { CartTotals } from './CartTotals';
import { CartUpsell } from './CartUpsell';
import { CartDiscount } from './CartDiscount';
import { FreeShippingMeter } from './FreeShippingMeter';
import { useGlobalContext } from '../../contexts';
import { useCartItems } from '../../hooks';
import { useIpFetcher } from '../../hooks/useIpFetcher';

export function Cart() {
  const cartItems = useCartItems();
  const settings = useSettings();
  const { asPath } = useRouter();
  const {
    state: { cartOpen },
    actions: { closeCart },
  } = useGlobalContext();
  const [isUpdating, setIsUpdating] = useState(false);

  const isCartPage = () => {
    return asPath === '/cart';
  };

  const cartSettings = settings?.cart;
  const enabledUpsell = cartSettings?.upsell?.enabled;
  const shippingBarSettings = cartSettings?.shippingBar;
  const beamSettings = settings?.beam;

  const hasSubscriptionItems = cartItems?.some(
    (item) => item.sellingPlanAllocation
  );
  const hasOnetimeItems = cartItems?.some(
    (item) => !item.sellingPlanAllocation
  );

  // set shipping bar text and state variables
  let isOnlySubscriptionItems = hasSubscriptionItems && !hasOnetimeItems;
  const defaultText =
    'Switch to subscription for 10% savings and free shipping (US)';
  let shippingBarText = shippingBarSettings?.onetimeOnly || defaultText;
  if (hasSubscriptionItems && hasOnetimeItems) {
    shippingBarText = shippingBarSettings?.mixedCart || defaultText;
  } else if (hasSubscriptionItems) {
    shippingBarText =
      shippingBarSettings?.subscriptionOnly || 'FREE SHIPPING (US ONLY)';
  }

  // check if the shipping statuse bar should display based on user location
  const { getUserLocationInfo } = useIpFetcher();
  const [shouldShowShippingBar, setShouldShowShippingBar] = useState(true);
  useEffect(() => {
    if (shippingBarSettings?.usOnly === true) {
      const userLocationInfo = getUserLocationInfo();
      if (
        userLocationInfo?.country_code &&
        userLocationInfo.country_code !== 'US'
      ) {
        setShouldShowShippingBar(false);
      }
    }
  }, [cartOpen]);

  const cartWrapper = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = cartWrapper.current;
    if (!element || !cartOpen) return undefined;

    const checkIsOverflowing = () => {
      if (!element) return;

      const newIsOverflowing = element.scrollHeight > element.clientHeight;

      setIsOverflowing((prev) =>
        prev !== newIsOverflowing ? newIsOverflowing : prev
      );
    };

    const throttledCheckIsOverflowing = throttle(checkIsOverflowing, 100);

    const resizeObserver = new ResizeObserver(() => {
      throttledCheckIsOverflowing();
    });

    const mutationObserver = new MutationObserver(() => {
      throttledCheckIsOverflowing();
    });

    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
    });

    resizeObserver.observe(element);

    // Initial check
    checkIsOverflowing();

    // Cleanup observers
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (throttledCheckIsOverflowing.cancel) {
        throttledCheckIsOverflowing.cancel();
      }
    };
  }, [cartOpen]);

  return (
    <aside
      className={`group fixed left-full top-0 z-[70] flex h-full w-full flex-col justify-between overflow-hidden bg-[#fff] text-[var(--primary)] transition md:max-w-[460px] ${
        cartOpen
          ? 'pointer-events-auto -translate-x-full'
          : 'pointer-events-none translate-x-0'
      }
      ${isCartPage() ? 'hidden' : null}
      ${isOverflowing ? 'is-overflowing' : ''}`}
    >
      {cartOpen && !isCartPage() && (
        <>
          <CartHeader
            closeCart={closeCart}
            heading={settings?.cart?.heading}
            announcement={settings?.cart?.announcement}
          />

          <FreeShippingMeter />

          {!!cartItems?.length && !!shouldShowShippingBar && (
            <div className="px-[18px]">
              <div
                className={`border-b-2 pb-2 text-center text-xs ${
                  isOnlySubscriptionItems
                    ? 'border-[var(--success)]'
                    : 'relative border-[#E9E9EB] before:absolute before:-bottom-[2px] before:block before:h-[2px] before:w-1/2 before:bg-warning'
                }`}
              >
                <div className="flex justify-start">
                  <span className="mr-2 flex items-center">
                    <span
                      className={`block h-2 w-2 rounded-lg ${
                        isOnlySubscriptionItems
                          ? 'bg-[var(--success)]'
                          : 'bg-[var(--warning)]'
                      }`}
                    />
                  </span>
                  <p className="font-normal text-[#343B49]">
                    {shippingBarText}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div
            id="cart-wrapper"
            ref={cartWrapper}
            className="cart__wrapper relative flex-1 overflow-y-auto scrollbar-hide"
          >
            <ul>
              {cartItems?.length
                ? cartItems.map((item) => {
                    return (
                      <li key={item.id} className="">
                        <CartItem
                          item={item}
                          closeCart={closeCart}
                          setIsUpdating={setIsUpdating}
                        />
                      </li>
                    );
                  })
                : !isUpdating && <CartEmpty closeCart={closeCart} />}
            </ul>
            {!!beamSettings && !!cartItems?.length && (
              <div className="p-4">
                <div className="border border-[#E9E9EB] bg-[#F2F7FB] p-[18px]">
                  <BeamSelectNonprofit />
                </div>
              </div>
            )}
          </div>

          <div className="relative group-[.is-overflowing]:shadow-[0px_0px_20px_0px_rgba(0,0,0,0.25)]">
            {enabledUpsell && <CartUpsell closeCart={closeCart} />}

            {!!cartItems?.length && <CartDiscount />}

            <CartTotals />
          </div>
        </>
      )}
    </aside>
  );
}

Cart.displayName = 'Cart';
